// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { wishListCardStyles } from "@ces/Overrides/Components/WishListCard";
import CartFilled from "@ces/XDL/Icons/CartFilled";
import StarX from "@ces/XDL/Icons/StarX";
import Trash from "@ces/XDL/Icons/Trash";
import { fontConfig } from "@ces/XDL/PreStyle/typography";
import { color, createLinkIconStyles, ellipsis, moreActionsSharedStyle, xScreen } from "@ces/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import Star from "@insite/mobius/Icons/Star";
import Tag from "@insite/mobius/Icons/Tag";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

wishListCardStyles.container = {
    css: css`
        display: grid;
        grid-template-columns: minmax(0, 452px) minmax(0, 430px) minmax(165px, 1fr);
        grid-column-gap: 20px;
        justify-content: space-between;
        ${xScreen("lte", "md")} {
            grid-template-columns: minmax(0, 226px) minmax(0, 1fr);
        }
        ${xScreen("lt", "sm")} {
            grid-template-columns: 1fr;
        }
        grid-row-gap: 12px;
        width: 100%;
    `,
};

// Description Section Styles
wishListCardStyles.descriptionWrapper = {
    css: css`
        grid-column: 1 / 2;
        grid-row: 1 / 2;
        > div {
            display: flex;
            flex-direction: column;
            gap: 12px;
            max-width: 317px;
            ${xScreen("lte", "md")} {
                max-width: unset;
            }
            ${xScreen("lt", "sm")} {
                max-width: 100%;
            }
        }
    `,
};

wishListCardStyles.nameWrapper = {
    css: css`
        & > ${StyledButton} {
            font-weight: bold;
            ${TypographyStyle} {
                font-size: ${fontConfig.fontSize.regular}px;
                line-height: 19px;
            }
        }
    `,
};

wishListCardStyles.productImage = {
    css: css`
        width: 91px;
        height: 91px;
        border-radius: 2px;
        ${xScreen("lte", "md")} {
            width: 106px;
            height: 106px;
        }
        ${xScreen("lt", "sm")} {
            width: 70px;
            height: 70px;
        }
        img {
            height: auto;
            max-height: 100%;
            max-width: 100%;
            object-fit: contain;
        }
    `,
    errorTypographyProps: {
        size: 11,
        css: css`
            max-height: 65px;
        `,
    },
};

wishListCardStyles.lastUpdatedText = {
    size: 12,
    lineHeight: "13px",
};

wishListCardStyles.descriptionText = {
    size: 12,
    lineHeight: "17px",
    css: css`
        ${ellipsis(2)}
    `,
};

wishListCardStyles.actionFavoriteClickable = {
    mergeCss: true,
    color: "text.main",
    typographyProps: {
        weight: "normal",
        css: css`
            display: flex;
            align-items: center;
            width: auto;
            font-size: 12px;
            line-height: 17px;
        `,
    },
    css: css`
        &:hover {
            ${IconWrapper} {
                color: ${color("common.ui8")};
            }
        }
    `,
};

wishListCardStyles.favTagsWrapper = {
    css: css`
        display: flex;
        column-gap: 15px;
        flex-wrap: wrap;
        > div {
            line-height: 17px;
        }
    `,
};

wishListCardStyles.starIcon = {
    src: Star,
    size: 14,
    css: css`
        margin-right: 6px;
        opacity: 0.66;
    `,
};

wishListCardStyles.starXIcon = {
    src: StarX,
    color: "common.ui8",
    size: 14,
    css: css`
        margin-right: 6px;
        opacity: 1;
    `,
};

wishListCardStyles.tagTooltip = {
    mergeCss: true,
    cssOverrides: {
        tooltipClickable: css`
            display: flex;
            align-items: center;
            font-size: 12px;
            line-height: 17px;
            margin: 0;
            gap: 6px;
        `,
    },
};

wishListCardStyles.tagIcon = {
    src: Tag,
    size: 12,
};

// Image Section Styles
wishListCardStyles.imagesWrapper = {
    css: css`
        grid-column: 2 / 3;
        grid-row: 1 / 2;
        display: flex;
        flex-wrap: wrap;
        gap: 22px;
        ${xScreen("lte", "md")} {
            grid-column: 2 / 3;
        }
        ${xScreen("lt", "sm")} {
            gap: 16px;
            grid-column: 1 / 2;
            grid-row: 2 / 3;
        }
    `,
};

wishListCardStyles.productImageLink = {
    css: css`
        display: flex;
        align-items: flex-start;
        margin: 0;
        height: fit-content;
    `,
};

// actions Wrapper
wishListCardStyles.actionsWrapper = {
    css: css`
        grid-column: 3 / 4;
        grid-row: 1 / 2;
        display: flex;
        justify-content: flex-end;
        ${xScreen("lte", "md")} {
            grid-column: 1 / 3;
            grid-row: 2 / 3;
        }
        ${xScreen("lt", "sm")} {
            grid-column: 1 / 2;
            grid-row: 3 / 4;
        }
    `,
};

wishListCardStyles.actionGridMediumHidden = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 12px;
        ${xScreen("lte", "md")} {
            flex-direction: row;
            justify-content: space-between;
            flex-wrap: wrap;
            width: 100%;
        }
    `,
};

wishListCardStyles.overflowMenu = {
    ...moreActionsSharedStyle.overflowMenu,
};

wishListCardStyles.sharingStatus = {};

wishListCardStyles.actionAddToCartLink = {
    ...createLinkIconStyles({
        icon: CartFilled,
        typographyStyles: {
            css: css`
                white-space: nowrap;
            `,
        },
    }),
};

wishListCardStyles.actionDeleteLink = {
    ...createLinkIconStyles({
        icon: Trash,
        iconStyles: {
            size: 11,
        },
    }),
};
